import React, {useMemo, useState} from 'react';
import {WordList} from '../../domain/types';
import WordListBadges from './WordListBadges';
import {obfuscateWord} from './obfuscateWord';
import {
  DownloadIcon,
  EyeOffIcon,
  ListIcon,
  MinusIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  StarIcon,
  XIcon,
} from '../Icons';

interface WordListDetailProps {
  list: WordList | null;
  isLoading: boolean;
  /** Drives the empty state's wording: no lists at all reads differently from none selected. */
  hasAnyLists: boolean;
  isInCollection: boolean;
  isBusy: boolean;
  onEdit: (id: string) => void;
  onExportCsv: () => void;
  onSetDefault: () => void;
  onAddToCollection: (id: string) => void;
  onRemoveFromCollection: (id: string) => void;
}

const actionButton = 'inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1.5 '
  + 'text-xs font-medium text-gray-200 hover:border-gray-600 hover:bg-gray-800 transition-colors '
  + 'disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer';

const Shell: React.FC<{children: React.ReactNode}> = ({children}) => (
  <section className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
    {children}
  </section>
);

/**
 * The selected list's words.
 *
 * Words are the reason to open a list and everything else here is chrome, so the panel opens
 * straight onto them — obfuscated by default, because these lists are read over someone's shoulder
 * in an edit bay as often as not.
 */
const WordListDetail: React.FC<WordListDetailProps> = ({
  list,
  isLoading,
  hasAnyLists,
  isInCollection,
  isBusy,
  onEdit,
  onExportCsv,
  onSetDefault,
  onAddToCollection,
  onRemoveFromCollection,
}) => {
  const [search, setSearch] = useState('');
  const [obfuscate, setObfuscate] = useState(true);

  const words = useMemo(() => list?.words ?? [], [list]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? words.filter(word => word.toLowerCase().includes(query)) : words;
  }, [words, search]);

  if (!list) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-1 px-4 py-16 text-center">
          <ListIcon className="mb-2 w-6 h-6 text-gray-700" />
          <p className="text-sm font-medium text-gray-200">
            {hasAnyLists ? 'Nothing selected' : 'No word list yet'}
          </p>
          <p className="max-w-xs text-sm text-gray-500">
            {hasAnyLists
              ? 'Pick a list to read its words, export it, or make it a project’s default.'
              : 'Create a list, or add one from the marketplace, and its words show up here.'}
          </p>
        </div>
      </Shell>
    );
  }

  const isOwner = list.isOwner !== false;

  return (
    <Shell>
      <header className="px-4 py-3 border-b border-gray-800">
        <div className="flex items-start gap-2 min-w-0">
          <h2 className="truncate text-sm font-semibold text-gray-100" title={list.name}>
            {list.name}
          </h2>
          <span className="flex items-center gap-1.5 pt-0.5">
            <WordListBadges list={list} />
          </span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          {words.length} word{words.length === 1 ? '' : 's'}
          {list.creatorEmail && <> · by {list.creatorEmail}</>}
        </p>
        {list.description && (
          <p className="mt-2 text-xs text-gray-400">{list.description}</p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {isOwner && (
            <button type="button" onClick={() => onEdit(list.id)} disabled={isBusy} className={actionButton}>
              <PencilIcon className="w-3.5 h-3.5" />
              Edit words
            </button>
          )}
          <button
            type="button"
            onClick={onSetDefault}
            disabled={isBusy}
            title="Use this list by default when converting in a project"
            className={actionButton}
          >
            <StarIcon className="w-3.5 h-3.5" />
            Set as default
          </button>
          <button
            type="button"
            onClick={onExportCsv}
            disabled={isBusy || words.length === 0}
            title="Download this list as a CSV"
            className={actionButton}
          >
            <DownloadIcon className="w-3.5 h-3.5" />
            Export
          </button>
          {isInCollection ? (
            !isOwner && (
              <button
                type="button"
                onClick={() => onRemoveFromCollection(list.id)}
                disabled={isBusy}
                className={actionButton}
              >
                <MinusIcon className="w-3.5 h-3.5" />
                Remove from my lists
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={() => onAddToCollection(list.id)}
              disabled={isBusy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs
               font-medium text-white hover:bg-indigo-500 transition-colors
               disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add to my lists
            </button>
          )}
        </div>
      </header>

      {words.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-gray-800
         bg-gray-950/40">
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search words…"
              aria-label="Search words in this list"
              className="w-48 rounded-lg border border-gray-800 bg-gray-950/60 py-1.5 pl-8 pr-7 text-xs
               text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
               focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:hidden"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear word search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300 cursor-pointer"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-400 hover:text-gray-300">
            <input
              type="checkbox"
              checked={obfuscate}
              onChange={event => setObfuscate(event.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer rounded border-gray-700 bg-gray-950 text-indigo-600
               focus:ring-1 focus:ring-indigo-500"
            />
            <EyeOffIcon className="w-3.5 h-3.5" />
            <span className="select-none">Obfuscate</span>
          </label>
        </div>
      )}

      <div className="max-h-[28rem] overflow-y-auto px-4 py-3">
        {isLoading && words.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">Loading words…</p>
        )}

        {!isLoading && words.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">
            This list has no words yet.
          </p>
        )}

        {words.length > 0 && filtered.length === 0 && (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-400">No words match “{search.trim()}”.</p>
            <button
              type="button"
              onClick={() => setSearch('')}
              className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
            >
              Clear search
            </button>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {filtered.map((word, index) => (
              <span
                key={`${word}-${index}`}
                title={obfuscate ? undefined : word}
                className="rounded-md bg-gray-800/70 px-2 py-1 font-mono text-xs text-gray-300"
              >
                {obfuscate ? obfuscateWord(word) : word}
              </span>
            ))}
          </div>
        )}
      </div>

      {words.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-800 text-xs tabular-nums text-gray-600">
          {search.trim()
            ? `Showing ${filtered.length} of ${words.length} words`
            : `${words.length} word${words.length === 1 ? '' : 's'}`}
        </div>
      )}
    </Shell>
  );
};

export default WordListDetail;
